import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Button, CloseIcon, IconButton } from '@resiliatech/birdsong'
import {
  Container,
  Popover,
  TabsContainer,
  Tab,
  Content,
  Dropzone,
  UploadIcon,
  UploadText,
  UploadLink,
  HelperText,
  UrlUploadContainer,
  UrlInput,
  UrlSubmitButton,
  ProgressContainer,
  ProgressBar,
  ProgressFill,
  ErrorContainer,
  ErrorTitle,
  ErrorDetails,
  ErrorDismissButton,
  ButtonContainer,
} from './CloudinaryCustomUploads.styled'
import {
  HeaderContainer,
  TitleSection,
} from '../Components/Pages/Impact/GoalPage/ProgressUpdate/ProgressUpdateDialog/ProgressUpdateDialog.styled'
import { HeaderAndButtonContainer } from '../Components/Pages/Impact/GoalPage/Updates/UpdatesTab.styled'

interface CloudinaryUploadResponse {
  secure_url: string
  public_id: string
  format: string
  width: number
  height: number
  original_filename: string
  resource_type: string
}

interface CloudinaryCustomUploadsProps {
  cloudName: string
  uploadPreset: string
  onSuccess?: (response: CloudinaryUploadResponse) => void
  onError?: (error: Error) => void
}

interface UploadError {
  message: string
  code?: string
  details?: string
}

const CloudinaryCustomUploads: React.FC<CloudinaryCustomUploadsProps> = ({
  cloudName,
  uploadPreset,
  onSuccess,
  onError,
}) => {
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<UploadError | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('local')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const urlInputRef = useRef<HTMLInputElement>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleError = useCallback(
    (errorMessage: string, code?: string, details?: string) => {
      const uploadError: UploadError = {
        message: errorMessage,
        code,
        details,
      }
      setError(uploadError)
      setUploading(false)
      onError?.(new Error(errorMessage))
    },
    [onError]
  )

  const clearError = () => {
    setError(null)
  }

  // Create permanent anchor element for the popover
  useEffect(() => {
    // First check if we already have an anchor element with this ID
    let anchorElement = document.getElementById('cloudinary-popover-anchor')

    // If not, create a new one
    if (!anchorElement) {
      anchorElement = document.createElement('div')
      anchorElement.style.position = 'fixed'
      anchorElement.style.top = '50%'
      anchorElement.style.left = '50%'
      anchorElement.style.width = '0'
      anchorElement.style.height = '0'
      anchorElement.style.pointerEvents = 'none'
      anchorElement.id = 'cloudinary-popover-anchor'
      document.body.appendChild(anchorElement)
    }

    // Set the anchor element in state
    setAnchorEl(anchorElement)

    // Clean up function
    return () => {
      // No need to remove the element on unmount as it might be used by other instances
      // Just ensure we clean up our state reference
      setAnchorEl(null)
    }
  }, [])

  const uploadToCloudinary = async (file: File) => {
    if (!cloudName || !uploadPreset) {
      handleError('Cloud name and upload preset are required')
      return
    }

    try {
      setUploading(true)
      setProgress(10)

      // Create FormData for the upload
      const formData = new FormData()
      formData.append('file', file)
      formData.append('upload_preset', uploadPreset)
      formData.append('cloud_name', cloudName)

      // Upload directly to Cloudinary
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`,
        {
          method: 'POST',
          body: formData,
        }
      )

      setProgress(90)

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'Upload failed')
      }

      const result = await response.json()

      // Format the response to match the expected structure
      const uploadResponse: CloudinaryUploadResponse = {
        secure_url: result.secure_url,
        public_id: result.public_id,
        format: result.format,
        width: result.width,
        height: result.height,
        original_filename: result.original_filename || file.name,
        resource_type: result.resource_type,
      }

      setProgress(100)
      onSuccess?.(uploadResponse)
      closePopover()
    } catch (err) {
      if (err instanceof Error) {
        handleError('Upload failed', 'UPLOAD_ERROR', err.message)
      } else {
        handleError(
          'Upload failed',
          'UPLOAD_ERROR',
          'An unknown error occurred'
        )
      }
    } finally {
      setUploading(false)
    }
  }

  const uploadFromUrl = async () => {
    if (!urlInputRef.current?.value) {
      handleError('Please enter a valid URL')
      return
    }

    if (!cloudName || !uploadPreset) {
      handleError('Cloud name and upload preset are required')
      return
    }

    const url = urlInputRef.current.value.trim()
    try {
      setUploading(true)
      setProgress(10)

      // Create FormData for the upload
      const formData = new FormData()
      formData.append('file', url)
      formData.append('upload_preset', uploadPreset)
      formData.append('cloud_name', cloudName)

      // Upload directly to Cloudinary
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`,
        {
          method: 'POST',
          body: formData,
        }
      )

      setProgress(90)

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'Upload failed')
      }

      const result = await response.json()

      // Format the response to match the expected structure
      const uploadResponse: CloudinaryUploadResponse = {
        secure_url: result.secure_url,
        public_id: result.public_id,
        format: result.format,
        width: result.width,
        height: result.height,
        original_filename: result.original_filename || 'remote_image',
        resource_type: result.resource_type,
      }

      setProgress(100)
      onSuccess?.(uploadResponse)
      closePopover()
    } catch (err) {
      if (err instanceof Error) {
        handleError('URL upload failed', 'URL_UPLOAD_ERROR', err.message)
      } else {
        handleError(
          'URL upload failed',
          'URL_UPLOAD_ERROR',
          'An unknown error occurred'
        )
      }
    } finally {
      setUploading(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target
    if (files && files.length > 0) {
      uploadToCloudinary(files[0])
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const { files } = e.dataTransfer
    if (files && files.length > 0) {
      uploadToCloudinary(files[0])
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const openPopover = () => {
    setIsOpen(true)
    clearError()
  }

  const closePopover = () => {
    setIsOpen(false)
    setUploading(false)
    setProgress(0)
    clearError()
  }

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
  }

  return (
    <Container>
      {error && (
        <ErrorContainer>
          <ErrorTitle>{error.message}</ErrorTitle>
          {error.details && <ErrorDetails>{error.details}</ErrorDetails>}
          <ErrorDismissButton onClick={clearError}>Dismiss</ErrorDismissButton>
        </ErrorContainer>
      )}

      <HeaderAndButtonContainer>
        <div />
        <ButtonContainer>
          <Button variant='table' label='Upload' onClick={openPopover} />
        </ButtonContainer>
      </HeaderAndButtonContainer>

      {uploading && (
        <ProgressBar>
          <ProgressFill progress={progress} />
        </ProgressBar>
      )}

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        BackdropProps={{
          invisible: false,
          style: { backgroundColor: 'rgba(26, 26, 26, 0.5)' },
        }}
      >
        <HeaderContainer>
          <TitleSection style={{ margin: '30px 0 10px 30px' }}>
            Add Attachment
          </TitleSection>
          <IconButton
            onClick={closePopover}
            size='small'
            style={{ marginRight: '40px' }}
          >
            <CloseIcon />
          </IconButton>
        </HeaderContainer>

        {/* Tabs */}
        <TabsContainer>
          <Tab
            active={activeTab === 'local'}
            role='tab'
            tabIndex={0}
            aria-selected={activeTab === 'local'}
            onClick={() => handleTabChange('local')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleTabChange('local')
              }
            }}
            style={{ marginLeft: '40px' }}
          >
            My Files
          </Tab>
          <Tab
            active={activeTab === 'url'}
            role='tab'
            tabIndex={0}
            aria-selected={activeTab === 'url'}
            onClick={() => handleTabChange('url')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleTabChange('url')
              }
            }}
          >
            Web Address (URL)
          </Tab>
        </TabsContainer>

        {/* Content area */}
        <Content>
          {activeTab === 'local' && (
            <Dropzone
              role='button'
              tabIndex={0}
              onClick={triggerFileInput}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  triggerFileInput()
                }
              }}
            >
              <UploadIcon
                viewBox='0 0 24 24'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                <polyline points='17 8 12 3 7 8' />
                <line x1='12' y1='3' x2='12' y2='15' />
              </UploadIcon>
              <UploadText>
                <UploadLink>Browse</UploadLink> to upload or drag and drop here
              </UploadText>
              <HelperText>
                Supported formats: JPG, PNG, GIF, SVG, MP4, MOV, PDF
              </HelperText>
              <input
                ref={fileInputRef}
                type='file'
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept='image/*,video/*,application/pdf'
              />
            </Dropzone>
          )}

          {activeTab === 'url' && (
            <UrlUploadContainer>
              <h4>Upload from URL</h4>
              <UploadText>
                Paste image or file URL to upload directly from the web
              </UploadText>
              <UrlInput
                ref={urlInputRef}
                type='text'
                placeholder='https://example.com/image.jpg'
              />
              <UrlSubmitButton
                type='button'
                onClick={uploadFromUrl}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </UrlSubmitButton>
            </UrlUploadContainer>
          )}

          {uploading && (
            <ProgressContainer>
              <p>Uploading...</p>
              <ProgressBar>
                <ProgressFill progress={progress} />
              </ProgressBar>
            </ProgressContainer>
          )}
        </Content>
      </Popover>
    </Container>
  )
}

export default CloudinaryCustomUploads
